import {ModerationSection} from "components/ModerationSection";
import React, {FC, useEffect, useMemo, useRef, useState} from "react";
import {DuplicateUser, ModerationUser} from "interfaces";
import {ModerationHint} from "components/ModerationHint";
import {AppNames, RootRoutes, toStatusString} from "enums";
import {AutoSizer, CellMeasurer, CellMeasurerCache, List} from "react-virtualized";

interface Props {
    user: ModerationUser
}

export const ModerationDuplicates: FC<Props> = ({user}) => {
    const [isVisible, setIsVisible] = useState(false);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const listRef = useRef<List | null>(null);
    const rowCacheRef = useRef(
        new CellMeasurerCache({
            defaultHeight: 260,
            fixedWidth: true
        })
    );

    const faces = user.duplicateFaces;
    const isSuspect = !!faces?.length;

    const getUserMaxSimilarity = (duplicateUser: DuplicateUser) => {
        if (!duplicateUser.faces?.length) {
            return 0;
        }
        return Math.max(...duplicateUser.faces.map(f => Math.round(f.similarity)));
    };

    const people = useMemo(() => {
        const duplicateFaces = faces ?? [];
        return duplicateFaces
            .reduce(
                (previousValue, currentValue) =>
                    [
                        ...previousValue,
                        ...currentValue.users.filter(
                            u => !previousValue.find(pv => pv.user.key === u.user.key)
                        )
                    ],
                [] as DuplicateUser[]
            )
            .filter(f => f.user.key !== user.id)
            .sort((a, b) => getUserMaxSimilarity(b) - getUserMaxSimilarity(a));
    }, [faces, user.id]);

    const totalRows = people.length;

    useEffect(() => {
        rowCacheRef.current.clearAll();
        listRef.current?.recomputeRowHeights(0);
    }, [people]);

    const onClick = (id: string) => {
        window.open(`${RootRoutes.Profile}/${id}`, '_blank', 'rel=noopener noreferrer')
    }

    if (!user.ipAddresses) {
        return null;
    }

    if (people.length < 1) {
        return null;
    }

    return <>
        <ModerationSection label={`Дубликаты (${people.length})`}>
            {isSuspect && <ModerationHint className="alert-danger mt-0">У пользователя есть дубликаты</ModerationHint>}
            <button
                type="button"
                className="text-black-50 text-decoration-underline btn btn-link p-0"
                style={{cursor: 'pointer'}}
                onClick={() => {
                    setIsVisible(prev => !prev)
                }}>
                {isVisible ? 'Скрыть' : 'Показать'}
            </button>
            {isVisible &&
                <div
                    className="table-responsive mt-1"
                    style={{height: 500}}
                    ref={containerRef}
                >
                    <AutoSizer>
                        {({height, width}: any) => (
                            <List
                                ref={listRef}
                                width={width}
                                height={height}
                                rowCount={totalRows}
                                deferredMeasurementCache={rowCacheRef.current}
                                rowHeight={rowCacheRef.current.rowHeight}
                                overscanRowCount={5}
                                rowRenderer={({index, key, parent, style}: any) => {
                                    const v = people[index];
                                    const similarity = getUserMaxSimilarity(v);
                                    const userPhotoKeys = user.duplicateFaces?.filter(f => f.users.filter(u => u.user.key === v.user.key).length > 0).map(df => df.photoKey);
                                    const userPhotos = userPhotoKeys ? user.photos?.filter(p => userPhotoKeys.includes(p.key)) : [];

                                    return (
                                        <CellMeasurer
                                            key={key}
                                            cache={rowCacheRef.current}
                                            columnIndex={0}
                                            rowIndex={index}
                                            parent={parent}
                                        >
                                            <div style={style}>
                                                <table
                                                    className="table table-light w-100 mb-0 align-middle"
                                                    style={{tableLayout: 'fixed', width: '100%'}}
                                                >
                                                    <colgroup>
                                                        <col style={{width: 212, maxWidth: 212}}/>
                                                        <col/>
                                                    </colgroup>
                                                    <tbody>
                                                    <tr
                                                        onClick={() => onClick(v.user.key)}
                                                        style={{cursor: 'pointer'}}
                                                    >
                                                        <td style={{wordBreak: 'break-word'}}>
                                                            <div className="fw-bold">
                                                                {v.user.name} {v.user.appId ? `(${AppNames[v.user.appId]})` : undefined}
                                                            </div>
                                                            <small>{toStatusString(v.user.status)}</small><br/>
                                                            <small>Совпадение <span
                                                                className={`badge ${similarity > 90 ? 'bg-warning' : 'bg-secondary'}`}>{similarity} %</span></small>
                                                            <br/>
                                                            <div className="mt-2 mb-1">Фото-триггеры</div>
                                                            <div
                                                                className="d-flex flex-row flex-wrap w-full"
                                                                style={{
                                                                    gap: 4,
                                                                    maxWidth: 212,
                                                                    width: 212,
                                                                }}
                                                            >
                                                                {userPhotos?.map(up =>
                                                                    <img
                                                                        key={up.key}
                                                                        src={up.image[0].link}
                                                                        alt={`Pic ${up.key}`}
                                                                        width={100}
                                                                        height={100}
                                                                        style={{objectFit: 'cover'}}
                                                                        className="rounded border border-secondary"
                                                                    />
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td style={{verticalAlign: 'top'}}>
                                                            <div
                                                                className="d-flex flex-row flex-wrap px-4"
                                                                style={{gap: 4, minWidth: 0}}
                                                            >
                                                                {v.faces.map(f =>
                                                                    <img
                                                                        key={f.photoUrl}
                                                                        src={f.photoUrl}
                                                                        alt={`Pic ${f.photoUrl}`}
                                                                        width={100}
                                                                        height={100}
                                                                        style={{
                                                                            objectFit: 'cover',
                                                                            display: 'block'
                                                                        }}
                                                                        className="rounded border border-secondary"
                                                                    />
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </CellMeasurer>
                                    );
                                }}
                            />
                        )}
                    </AutoSizer>
                </div>
            }
        </ModerationSection>
    </>
}